import { useCallback, useMemo } from "react";
import { useCacheMutations, useCacheSlice } from "../hooks";

/** Resource returned by GET /v2/restricted-words. */
export type RestrictedWordRule = {
	id: string;
	object: "restricted_word";
	regexPattern: string;
	displayName: string;
	suggestion: string | null;
	warning: boolean;
	disabled: boolean;
	clientCode: string | null;
	matterId: string | null;
	createdAt: string;
	updatedAt: string;
};

/** Processed restricted word for pattern matching. */
export type RestrictedWord = {
	word: string;
	pattern: RegExp;
	displayName: string;
	suggestion: string | null;
	severity: "warning" | "block";
};

function processRules(rules: RestrictedWordRule[]): RestrictedWord[] {
	const result: RestrictedWord[] = [];
	for (const rule of rules) {
		if (rule.disabled) continue;
		try {
			result.push({
				word: rule.regexPattern,
				pattern: new RegExp(rule.regexPattern, "gi"),
				displayName: rule.displayName,
				suggestion: rule.suggestion,
				severity: rule.warning ? "warning" : "block",
			});
		} catch {
			// Invalid patterns are ignored defensively; the API validates new writes.
		}
	}
	return result;
}

type ScopedRules = {
	firm: RestrictedWordRule[];
	clients: Map<string, RestrictedWordRule[]>;
	matters: Map<string, RestrictedWordRule[]>;
};

function groupByScope(rules: RestrictedWordRule[]): ScopedRules {
	const firm: RestrictedWordRule[] = [];
	const clients = new Map<string, RestrictedWordRule[]>();
	const matters = new Map<string, RestrictedWordRule[]>();

	for (const rule of rules) {
		if (rule.clientCode && rule.matterId) {
			const key = `${rule.clientCode}.${rule.matterId}`;
			const scopedRules = matters.get(key);
			if (scopedRules) scopedRules.push(rule);
			else matters.set(key, [rule]);
		} else if (rule.clientCode) {
			const scopedRules = clients.get(rule.clientCode);
			if (scopedRules) scopedRules.push(rule);
			else clients.set(rule.clientCode, [rule]);
		} else {
			firm.push(rule);
		}
	}

	return { firm, clients, matters };
}

function mergeWithPrecedence(layers: RestrictedWordRule[][]): RestrictedWord[] {
	const seen = new Map<string, RestrictedWordRule>();

	for (const layer of layers) {
		for (const rule of layer) {
			const key = rule.regexPattern.toLowerCase();
			if (!seen.has(key)) seen.set(key, rule);
		}
	}

	return processRules(Array.from(seen.values()));
}

export function useRestrictedWords() {
	const { data, isLoading, error } = useCacheSlice("restricted_words");
	const { refresh } = useCacheMutations();
	const rules = Array.isArray(data) ? (data as RestrictedWordRule[]) : [];
	const scoped = useMemo(() => groupByScope(rules), [rules]);

	const getRestrictedWords = useCallback(
		(client?: string, matter?: string): RestrictedWord[] => {
			const layers: RestrictedWordRule[][] = [];
			if (client && matter) {
				layers.push(scoped.matters.get(`${client}.${matter}`) ?? []);
			}
			if (client) layers.push(scoped.clients.get(client) ?? []);
			layers.push(scoped.firm);
			return mergeWithPrecedence(layers);
		},
		[scoped],
	);

	/** Return active firm, client, and optionally matter rules from the list cache. */
	const fetchAllForClient = useCallback(
		async (
			clientCode: string,
			matterCode?: string,
		): Promise<RestrictedWord[]> => {
			const selected = rules.filter(
				(rule) =>
					rule.clientCode === null ||
					(rule.clientCode === clientCode &&
						(!matterCode ||
							rule.matterId === null ||
							rule.matterId === matterCode)),
			);
			return processRules(selected);
		},
		[rules],
	);

	return {
		rules,
		getRestrictedWords,
		fetchAllForClient,
		isLoading,
		error,
		refresh: () => refresh("restricted_words"),
	};
}
